import { GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';

import {
  BmsHeroDocument,
  BmsHeroQuery,
  BmsHeroQueryVariables,
  BmsIconCardsDocument,
  BmsIconCardsQuery,
  BmsIconCardsQueryVariables,
  BmsLargeImageSectionDocument,
  BmsLargeImageSectionQuery,
  BmsLargeImageSectionQueryVariables,
  BmsPartnersBlogSectionDocument,
  BmsPartnersBlogSectionQuery,
  BmsPartnersBlogSectionQueryVariables,
  BmsProductImageBlockDocument,
  BmsProductImageBlockQuery,
  BmsProductImageBlockQueryVariables,
  BmsProductSpecsBlockDocument,
  BmsProductSpecsBlockQuery,
  BmsProductSpecsBlockQueryVariables,
  BmsSeoDocument,
  BmsSeoQuery,
  BmsSeoQueryVariables,
  BmsTwoImagesGridDocument,
  BmsTwoImagesGridQuery,
  BmsTwoImagesGridQueryVariables,
  FooterDocument,
  FooterQuery,
  FooterQueryVariables,
  FormDocument,
  FormQuery,
  FormQueryVariables,
  NavigationDocument,
  NavigationQuery,
  NavigationQueryVariables,
  TwoImagesGridFragment,
} from '@/api';
import { addApolloState, initializeApollo } from '@/api/apolloClient';
import { Layout } from '@/components/layout/Layout';
import { Partners } from '@/components/partners/Partners';
import { Seo } from '@/components/Seo';
import EnhancedProductImageBlock from '@/components/shared/EnhancedProductImageBlock';
import EnhancedProductSpecsBlock from '@/components/shared/EnhancedProductSpecsBlock';
import HeroVideoSection from '@/components/shared/HeroVideoSection';
import IconCards from '@/components/shared/IconCards';
import LargeImage from '@/components/shared/LargeImage';
import TwoImagesGrid from '@/components/shared/TwoImagesGrid';

export const getStaticProps: GetStaticProps = async () => {
  const client = initializeApollo();

  // prefetch
  await client.query({ query: BmsSeoDocument });
  await client.query({ query: BmsHeroDocument });
  await client.query({ query: BmsIconCardsDocument });
  await client.query({ query: BmsProductImageBlockDocument });
  await client.query({ query: BmsProductSpecsBlockDocument });
  await client.query({ query: BmsTwoImagesGridDocument });
  await client.query({ query: BmsLargeImageSectionDocument });
  await client.query({ query: BmsPartnersBlogSectionDocument });
  await client.query({ query: NavigationDocument });
  await client.query({ query: FooterDocument });
  await client.query({ query: FormDocument });

  const { data: seoData } = await client.query<
    BmsSeoQuery,
    BmsSeoQueryVariables
  >({
    query: BmsSeoDocument,
  });

  const { data: heroData } = await client.query<
    BmsHeroQuery,
    BmsHeroQueryVariables
  >({ query: BmsHeroDocument });

  const { data: cardsData } = await client.query<
    BmsIconCardsQuery,
    BmsIconCardsQueryVariables
  >({ query: BmsIconCardsDocument });

  const { data: productImageBlockData } = await client.query<
    BmsProductImageBlockQuery,
    BmsProductImageBlockQueryVariables
  >({
    query: BmsProductImageBlockDocument,
  });

  const { data: productSpecsBlockData } = await client.query<
    BmsProductSpecsBlockQuery,
    BmsProductSpecsBlockQueryVariables
  >({
    query: BmsProductSpecsBlockDocument,
  });

  const { data: twoImagesGridData } = await client.query<
    BmsTwoImagesGridQuery,
    BmsTwoImagesGridQueryVariables
  >({
    query: BmsTwoImagesGridDocument,
  });

  const { data: largeImageSectionData } = await client.query<
    BmsLargeImageSectionQuery,
    BmsLargeImageSectionQueryVariables
  >({
    query: BmsLargeImageSectionDocument,
  });

  const { data: partnersBlogSectionData } = await client.query<
    BmsPartnersBlogSectionQuery,
    BmsPartnersBlogSectionQueryVariables
  >({ query: BmsPartnersBlogSectionDocument });

  const { data: formData } = await client.query<FormQuery, FormQueryVariables>({
    query: FormDocument,
  });

  const { data: footerData } = await client.query<
    FooterQuery,
    FooterQueryVariables
  >({ query: FooterDocument });

  const { data: headerData } = await client.query<
    NavigationQuery,
    NavigationQueryVariables
  >({ query: NavigationDocument });

  return addApolloState(client, {
    props: {
      seoData,
      heroData,
      cardsData,
      productImageBlockData,
      productSpecsBlockData,
      twoImagesGridData,
      largeImageSectionData,
      partnersBlogSectionData,
      headerData,
      footerData,
      formData,
    },
  });
};

const BMS: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  seoData,
  heroData,
  cardsData,
  productImageBlockData,
  productSpecsBlockData,
  twoImagesGridData,
  largeImageSectionData,
  partnersBlogSectionData,
  headerData,
  footerData,
  formData,
}) => (
  <Layout formData={formData} headerData={headerData} footerData={footerData}>
    <Seo seo={seoData?.bms?.data?.attributes?.seo} />
    <HeroVideoSection
      hero={heroData?.bms?.data?.attributes?.heroVideoSection}
      subtitleMaxWidth={540}
    />
    <IconCards
      cards={cardsData?.bms?.data?.attributes?.cardsSection}
      titleMaxWidth={880}
    />
    <EnhancedProductImageBlock
      productImageBlock={
        productImageBlockData?.bms?.data?.attributes?.productImageBlock
      }
      parentIndex={1}
    />
    <EnhancedProductSpecsBlock
      productSpecsBlock={
        productSpecsBlockData?.bms?.data?.attributes?.productSpecsBlock
      }
      parentIndex={1}
    />
    {twoImagesGridData?.bms?.data?.attributes?.twoImagesGrid?.map(
      (section?: TwoImagesGridFragment | null, index?: number) => (
        <TwoImagesGrid
          key={`twoImagesGrid-bms-item-${index}`}
          twoImagesGrid={section}
          descriptionMaxWidth={index === 0 ? 450 : 1000}
        />
      ),
    )}
    <LargeImage
      largeImageSection={
        largeImageSectionData?.bms?.data?.attributes?.largeImageSection
      }
      descriptionMaxWidth={760}
    />
    <Partners
      data={partnersBlogSectionData?.bms?.data?.attributes?.partnersBlogSection}
    />
  </Layout>
);

export default BMS;
